//
// # The Schematic Data Model
//

// Local Imports
import { Context } from "CoreUtils";
import type { DepotGet } from "Depot";
import { schema } from "GenAlphaSchema";
import { Result } from "../errors";
import { Bbox } from "../core/bbox";
import { Xform } from "../core/xform";
import { Element, Rectangle, Ellipse, Polygon } from "../graphics/element";
import { Text } from "../graphics/text";
import { Symbol } from "./symbol";
import { SolderDot } from "./solderdot";
import { Instance } from "./instance";
import { Ident, IdentSet, IdentSpec, NonBuiltinIdent } from "../ident";
import { WireSegment } from "./wire";
import { SchPinSpec, SchPin } from "./schport";
import { authorship } from "./author";
import { Conns } from "./conns";
import { Builtin } from "../builtins/library";
import { randomId } from "../id";

export type SchematicJson = schema.Schematic;
export type Property = schema.Property;

// # Schematic
export interface Schematic {
  contentKind: "schematic"; // Content Kind Discriminator

  ident: Ident; // Identifier
  pdkId?: string; // PDK identifier (optional)

  // Core Content
  instances: Array<Instance>; // Instances
  pins: Array<SchPinSpec>; // Schematic Pins
  wireSegments: Array<WireSegment>; // Wire Segments
  graphics: Array<Element>; // Annotation-Only "Drawing" Graphics
  properties?: Array<Property>; // Properties

  // Computed Content
  dots: Array<SolderDot>; // Wire/ "Solder" Dots
  bbox: Bbox; // Bounding Box
  conns?: Conns; // Connectables

  // Symbols we depend upon, mapped from JSON-encoded `LibCellView`
  symbols: SymbolMap;

  // View references (optional, from schema)
  symbolImpl?: Ident; // Backward reference: The symbol this schematic implements
  layoutImpls?: Array<schema.schematic.LayoutImplRef>; // Forward references: Layouts that implement this schematic

  // Symmetry/grouping constraints for placement (passed through from schema)
  constraints?: schema.schematic.Constraints;
}

// # Schematic Namespace
export const Schematic = {
  // Create a new schematic. Requires a valid identifier.
  new: (ident: Ident): Schematic => ({
    contentKind: "schematic",
    ident: ident,
    bbox: Bbox.empty(),
    instances: [],
    pins: [],
    wireSegments: [],
    dots: [],
    symbols: new SymbolMap(),
    graphics: [],
  }),

  // Create a new `Schematic` from a partial definition.
  fromPartial: (partial: Partial<Schematic>): Schematic => {
    // Ensure we have a valid ident - if not provided, create a minimal one
    const ident = partial.ident || Ident.default();

    return {
      ...Schematic.new(ident),
      ...partial,
    };
  },

  // Convert to a JSON-serializable object
  toJson(sch: Schematic): SchematicJson {
    // Recompute bbox from actual content before serializing.
    sch.bbox = Schematic.calcBbox(sch);

    // Rip out the non-JSON-serializable fields.
    const { instances, symbols: _symbols, conns: _conns, ...rest } = sch;
    const json: SchematicJson = {
      // Take all the stuff from `sch`...
      ...rest,
      // And replace a few fields with JSON-serializable versions.
      instances: instances.map((inst) => Instance.toSpec(inst)),
      schemaVersion: schema.SCHEMA_VERSION,
    };
    // And then return as the `unknown` type that `JSON.stringify` wants.
    return json;
  },

  // Convert a schema.Schematic into the model form, resolving each
  // instance's target symbol via the depot. Mirrors `Layout.fromSchema`:
  // a parallel bulk fetch of unique target idents through `Symbol.getSymbol`,
  // then per-instance lookup from the now-populated cache.
  fromSchema: async (
    ctx: Context,
    schJson: SchematicJson,
    depot: DepotGet,
    namespace: string
  ): Promise<Result<Schematic, ImportError>> => {
    const { instances: instanceJsons, ...rest } = schJson;

    // Collect unique target idents and resolve in parallel.
    const targets = new IdentSet();
    for (const inst of instanceJsons) {
      targets.add(inst.target);
    }
    const targetList = targets.toArray();
    await Promise.all(
      targetList.map((ident) => Symbol.getSymbol(ctx, ident, depot, namespace))
    );

    // Populate the model's SymbolMap from cache. Lookups below hit memory.
    const symbols = new SymbolMap();
    for (const ident of targetList) {
      if (ident.kind === "builtin") continue;
      const resolved = Symbol.symbols.get(ident);
      if (resolved) symbols.insert(resolved);
    }

    // Convert each instance, calculating all its internal fields.
    const instances = instanceJsons.map((instJson) => {
      const symbol = symbols.lookup(instJson.target);
      if (!symbol) {
        console.warn(
          `Instance "${
            instJson.name
          }": symbol not found for target ${Ident.stringify(
            instJson.target
          )}, bbox will be empty`
        );
      }
      const instance = Instance.fromPartial({
        ...instJson,
        symbol,
        author: authorship(instJson.target),
        // `bbox` is optional in the persisted Symbol schema, so a resolved
        // symbol may still have no bbox (e.g. a symbol saved with only pins
        // and no graphics). Default to empty in that case too, otherwise the
        // undefined bbox crashes Xform.transformBbox in calcBbox below.
        bbox: symbol?.bbox ?? Bbox.empty(),
        evals: [],
      });
      return Instance.evaluateExprs(instance);
    });

    // Normalize pin labels to use the default format for each direction,
    // ignoring any persisted labelFmt from the saved file.
    const pins = rest.pins.map((pin) => {
      const normalized = SchPinSpec.new(
        pin.portName,
        pin.direction,
        Xform.loc(pin.xform),
        Xform.orientation(pin.xform)
        // labelFmt omitted — uses default for direction
      );
      normalized.id = pin.id;
      normalized.signalType = pin.signalType;
      return normalized;
    });

    // Build the schematic and compute its bbox from actual content.
    const schematic = Schematic.fromPartial({
      ...rest,
      pins,
      instances,
      symbols,
      // properties are handled fine thru ...rest
    });
    schematic.bbox = Schematic.calcBbox(schematic);
    return Result.Ok(schematic);
  },

  // Calculate the bounding box from all schematic entities.
  calcBbox: (sch: Schematic): Bbox => {
    const bboxes: Array<Bbox> = [];

    for (const inst of sch.instances) {
      bboxes.push(Xform.transformBbox(inst.xform, inst.bbox));
    }
    for (const pin of sch.pins) {
      bboxes.push(SchPin.calcBbox(pin));
    }
    for (const seg of sch.wireSegments) {
      bboxes.push(Bbox.fromTwoPoints(seg.endpoints));
    }
    for (const elem of sch.graphics) {
      bboxes.push(elementBbox(elem));
    }

    if (bboxes.length === 0) {
      return Bbox.empty();
    }
    return Bbox.getBboxUnion(bboxes);
  },

  // Assign unique IDs to all the elements in a schematic, preserving existing IDs.
  withIDs: (schematic: SchematicJson): SchematicJson => {
    // Assign random IDs to instances
    for (const instance of schematic.instances) {
      instance.id = instance.id ?? randomId();
    }
    // Assign random IDs to pins
    for (const pin of schematic.pins) {
      pin.id = pin.id ?? randomId();
    }
    // Assign random IDs to wire segments
    for (const wireSegment of schematic.wireSegments) {
      wireSegment.id = wireSegment.id ?? randomId();
    }
    // Note: graphics elements don't have IDs in the schema
    return schematic;
  },
};

export const SchematicJson = {
  new: (ident: Ident): SchematicJson => Schematic.toJson(Schematic.new(ident)),
  null: (): SchematicJson => Schematic.toJson(Schematic.fromPartial({})),
};

//
// # Map of `Symbol`s
// Largely our way to "hash" their identifiers.
//
export class SymbolMap {
  //
  private inner = new Map<string, Readonly<Symbol>>();
  readonly values = () => this.inner.values();

  // Hash key for a non-builtin identifier
  private key(ident: NonBuiltinIdent): string {
    return Ident.stringify(ident);
  }

  // Look up a symbol, returning undefined if it's not in the map.
  // Returns no indication of whether it might be found anywhere else.
  lookup = (ident: IdentSpec): Symbol | undefined => {
    const get = this.get(ident);
    if (get.answer === "here") return get.symbol;
    return undefined;
  };

  // Get a symbol, returning the 3-state `GetSymbol` indication of its availability.
  get = (ident: IdentSpec): GetSymbol => {
    if (ident.kind === "builtin") {
      const builtin = Builtin.find(ident.id);
      if (builtin) return { answer: "here", symbol: builtin.symbol };
      return { answer: "thats-not-a-thing" };
    }

    // Check the symbol map first
    const symbol = this.inner.get(this.key(ident));
    if (symbol) return { answer: "here", symbol };

    // Symbol not found in map
    // PDK symbols are pre-loaded by AlphaServer.loadPdkSymbols() before schematic is parsed
    // If a symbol is missing here, it means either:
    // 1. The symbol file doesn't exist in the depot
    // 2. The symbol wasn't pre-loaded (schematic loaded through different path)
    // 3. The symbol is a user-defined symbol that needs external fetching
    return { answer: "not-found-here" };
  };

  // Check if a (non-built-in) symbol is present in the map.
  has = (ident: NonBuiltinIdent): boolean => {
    return this.inner.has(this.key(ident));
  };

  // Insert a symbol into the map.
  insert = (symbol: Symbol): unknown => {
    const { ident } = symbol;
    if (ident.kind === "builtin") return; // Bail, no need to store these
    return this.inner.set(this.key(ident), symbol);
  };
}

// Three answers can come back when looking for an in-memory symbol:
//
// - "Here's the Symbol". I.e. success.
// - "That's not a thing". Generally for invalid builtins.
// - "Maybe a thing, but not found here". Generally for user-defined Symbols, which often need to be fetched externally.
//
export type GetSymbol =
  | { answer: "here"; symbol: Symbol }
  | { answer: "thats-not-a-thing" | "not-found-here" };

export type ImportError = {
  why: "duplicate-symbol";
  ident: NonBuiltinIdent;
};

// Compute the bounding box of a graphics Element (Shape | Text).
function elementBbox(elem: Element): Bbox {
  switch (elem.kind) {
    case "rectangle":
      return Rectangle.bbox(elem);
    case "polygon":
      return Polygon.bbox(elem);
    case "ellipse":
      return Ellipse.bbox(elem);
    case "path":
      return Bbox.fromPoints(...elem.points);
    case "text":
      return Text.bbox(elem);
    case "arc":
      return elem.arcBbox;
    default:
      return Bbox.empty();
  }
}
